"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { ExternalLink, KeyRound, Lock, Plus, RefreshCw, Send, ShieldCheck } from "lucide-react";
import { listWebhookEndpoints, type WebhookEndpoint } from "../../../services/merchantApi";
import { ErrorCard, InlineNotice, Section, focusRing, primaryBtn, secondaryBtn } from "../ui";
import { hostOf } from "./format";
import EndpointCard, { EndpointCardSkeleton } from "./EndpointCard";
import EndpointFormModal, { apiErrorOf } from "./EndpointFormModal";
import RotateSecretModal from "./RotateSecretModal";
import SecretRevealCard, { type SecretReveal } from "./SecretRevealCard";

// ────────────────────────────────────────────────────────────────────────────
// EndpointsTab — the "Endpoints" tab of /developers/webhooks: onboarding for
// a first-time merchant, the endpoint cards, create, the 5-endpoint cap and
// the one-time secret reveal.
//
// Data lives in useWebhookEndpoints() (owned by WebhooksPage, so the
// Deliveries tab can reuse the list and a revealed secret survives a tab
// switch). While this tab is visible the list refetches silently every 30 s
// WITHOUT stats and merges by id: no skeleton, no flicker, and an open
// dialog never loses its endpoint.
// ────────────────────────────────────────────────────────────────────────────

const POLL_MS = 30_000;
const DEFAULT_LIMIT = 5;
const ZERO_STATS = { succeeded: 0, failed: 0, inProgress: 0, skipped: 0 };

export interface EndpointsData {
  status: "loading" | "error" | "ready";
  endpoints: WebhookEndpoint[];
  limit: number;
  alertRecipient: string | null;
  secretWritesBlocked: boolean;
  error: { message: string; requestId: string | null } | null;
  /** A stats refresh (manual) is running. */
  refreshing: boolean;
}

export interface WebhookEndpointsStore {
  data: EndpointsData;
  /** "initial" shows the skeleton; "stats" is the manual refresh; "silent" is the poll. */
  reload: (mode: "initial" | "stats" | "silent") => Promise<void>;
  replaceEndpoint: (next: WebhookEndpoint) => void;
  removeEndpoint: (endpointId: string) => void;
  addEndpoint: (ep: WebhookEndpoint) => void;
  /**
   * After a create whose response never arrived: refetch and return the
   * endpoint that was not in `knownIds` (it was created), or null.
   */
  recoverCreate: (knownIds: Set<string>) => Promise<WebhookEndpoint | null>;
}

const INITIAL: EndpointsData = {
  status: "loading",
  endpoints: [],
  limit: DEFAULT_LIMIT,
  alertRecipient: null,
  secretWritesBlocked: false,
  error: null,
  refreshing: false,
};

/**
 * Endpoint list for the signed-in merchant.
 * @param poll refetch silently every 30 s while the document is visible
 */
export function useWebhookEndpoints(
  wallet: string,
  email: string | null,
  opts: { poll: boolean },
): WebhookEndpointsStore {
  const [data, setData] = useState<EndpointsData>(INITIAL);
  // Bumped by every local change: a response that was already on the wire
  // must not overwrite a newer optimistic / server-confirmed card.
  const version = useRef(0);
  // Bumped on identity change / unmount: stale answers are dropped.
  const generation = useRef(0);
  const silentInFlight = useRef(false);
  const lastFetchAt = useRef(0);

  const reload = useCallback(
    async (mode: "initial" | "stats" | "silent") => {
      if (mode === "silent" && silentInFlight.current) return;
      if (mode === "silent") silentInFlight.current = true;
      const gen = generation.current;
      const ver = version.current;
      // A first load starts from scratch (it also runs on an identity change,
      // and nothing of the previous account may linger).
      if (mode === "initial") setData(INITIAL);
      if (mode === "stats") setData((d) => ({ ...d, refreshing: true }));
      try {
        const res = await listWebhookEndpoints(wallet, email, { includeStats: mode !== "silent" });
        if (gen !== generation.current) return;
        lastFetchAt.current = Date.now();
        const fresh = Array.isArray(res.endpoints) ? res.endpoints : [];
        setData((cur) => {
          const curById = new Map(cur.endpoints.map((e) => [e.id, e]));
          let endpoints: WebhookEndpoint[];
          if (version.current !== ver) {
            // A local change landed meanwhile: keep the local copies and
            // take only the numbers from this response.
            const freshById = new Map(fresh.map((e) => [e.id, e]));
            endpoints = cur.endpoints.map((e) => ({
              ...e,
              stats24h: freshById.get(e.id)?.stats24h ?? e.stats24h,
            }));
          } else {
            endpoints = fresh.map((e) => ({ ...e, stats24h: e.stats24h ?? curById.get(e.id)?.stats24h }));
          }
          return {
            status: "ready",
            endpoints,
            limit: res.limit || DEFAULT_LIMIT,
            alertRecipient: res.alertRecipient ?? null,
            secretWritesBlocked: !!res.platform?.secretWritesBlocked,
            error: null,
            refreshing: false,
          };
        });
      } catch (err) {
        if (gen !== generation.current) return;
        const e = apiErrorOf(err);
        if (mode === "initial") {
          setData((d) => ({
            ...d,
            status: "error",
            error: { message: e.message || "Failed to load.", requestId: e.requestId ?? null },
            refreshing: false,
          }));
        } else {
          // Keep what is on screen. Only the manual refresh says anything.
          setData((d) => ({ ...d, refreshing: false }));
          if (mode === "stats") toast.error(e.message || "Couldn't refresh your endpoints.");
        }
      } finally {
        if (mode === "silent") silentInFlight.current = false;
      }
    },
    [wallet, email],
  );

  useEffect(() => {
    generation.current += 1;
    silentInFlight.current = false;
    void reload("initial");
    return () => {
      generation.current += 1;
    };
  }, [reload]);

  const ready = data.status === "ready";
  useEffect(() => {
    if (!opts.poll || !ready) return;
    // Returning to this tab after a while: catch up right away.
    if (Date.now() - lastFetchAt.current >= POLL_MS) void reload("silent");
    const id = setInterval(() => {
      if (!document.hidden) void reload("silent");
    }, POLL_MS);
    const onVisible = () => {
      if (!document.hidden && Date.now() - lastFetchAt.current >= POLL_MS) void reload("silent");
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [opts.poll, ready, reload]);

  const replaceEndpoint = useCallback((next: WebhookEndpoint) => {
    version.current += 1;
    setData((d) => ({ ...d, endpoints: d.endpoints.map((e) => (e.id === next.id ? next : e)) }));
  }, []);

  const removeEndpoint = useCallback((endpointId: string) => {
    version.current += 1;
    setData((d) => ({ ...d, endpoints: d.endpoints.filter((e) => e.id !== endpointId) }));
  }, []);

  const addEndpoint = useCallback((ep: WebhookEndpoint) => {
    version.current += 1;
    setData((d) => ({
      ...d,
      endpoints: d.endpoints.some((e) => e.id === ep.id)
        ? d.endpoints.map((e) => (e.id === ep.id ? ep : e))
        : [...d.endpoints, ep],
    }));
  }, []);

  const recoverCreate = useCallback(
    async (knownIds: Set<string>): Promise<WebhookEndpoint | null> => {
      const gen = generation.current;
      try {
        const res = await listWebhookEndpoints(wallet, email);
        if (gen !== generation.current) return null;
        const list = Array.isArray(res.endpoints) ? res.endpoints : [];
        const created = list.find((e) => !knownIds.has(e.id)) || null;
        version.current += 1;
        lastFetchAt.current = Date.now();
        setData((cur) => {
          const curById = new Map(cur.endpoints.map((e) => [e.id, e]));
          return {
            ...cur,
            status: "ready",
            endpoints: list.map((e) => ({
              ...e,
              stats24h: e.stats24h ?? curById.get(e.id)?.stats24h ?? (e.id === created?.id ? ZERO_STATS : undefined),
            })),
            limit: res.limit || cur.limit,
            alertRecipient: res.alertRecipient ?? cur.alertRecipient,
            secretWritesBlocked: !!res.platform?.secretWritesBlocked,
          };
        });
        return created;
      } catch {
        return null;
      }
    },
    [wallet, email],
  );

  return { data, reload, replaceEndpoint, removeEndpoint, addEndpoint, recoverCreate };
}

// ─── Tab ───────────────────────────────────────────────────────────────────

export interface EndpointsTabProps {
  wallet: string;
  email: string | null;
  store: WebhookEndpointsStore;
  /** List flag OR summary flag: creating endpoints / rotating secrets is paused. */
  secretWritesBlocked: boolean;
  /** Secrets waiting to be copied (owned by the page so a tab switch keeps them). */
  reveals: SecretReveal[];
  onReveal: (r: Omit<SecretReveal, "nonce">) => void;
  onDismissReveal: (endpointId: string) => void;
  /** A test delivery was created — the Deliveries tab should refetch page 1. */
  onTestSent: () => void;
}

export default function EndpointsTab({
  wallet,
  email,
  store,
  secretWritesBlocked,
  reveals,
  onReveal,
  onDismissReveal,
  onTestSent,
}: EndpointsTabProps) {
  const { data } = store;
  const [params, setParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  // Created, but the response (and so the secret) never reached us.
  const [lostId, setLostId] = useState<string | null>(null);
  const [rotateLostOpen, setRotateLostOpen] = useState(false);
  const knownIds = useRef<Set<string>>(new Set());

  const limit = data.limit || DEFAULT_LIMIT;
  const count = data.endpoints.length;
  const ready = data.status === "ready";
  const atLimit = ready && count >= limit;
  const canCreate = ready && !atLimit && !secretWritesBlocked;
  const lost = lostId ? data.endpoints.find((e) => e.id === lostId) || null : null;

  const openCreate = useCallback(() => {
    if (!canCreate) return;
    knownIds.current = new Set(data.endpoints.map((e) => e.id));
    setCreateOpen(true);
  }, [canCreate, data.endpoints]);

  // `?new=1` (API Settings "Add endpoint", docs) opens the create form once.
  useEffect(() => {
    if (params.get("new") !== "1" || !ready) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("new");
        return next;
      },
      { replace: true },
    );
    openCreate();
  }, [params, ready, setParams, openCreate]);

  const addButton = (
    <button
      type="button"
      onClick={openCreate}
      disabled={!canCreate}
      title={
        atLimit
          ? `You've reached the ${limit}-endpoint limit`
          : secretWritesBlocked
            ? "Creating endpoints is paused on our side right now"
            : undefined
      }
      className={`${secondaryBtn} shrink-0 py-2 disabled:cursor-not-allowed ${focusRing}`}
    >
      <Plus className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">Add endpoint</span>
      <span className="sm:hidden">Add</span>
    </button>
  );

  let body: ReactNode;
  if (data.status === "loading") {
    body = (
      <div className="space-y-3">
        <EndpointCardSkeleton />
        <EndpointCardSkeleton />
      </div>
    );
  } else if (data.status === "error") {
    body = (
      <ErrorCard
        title="Couldn't load your endpoints"
        message={data.error?.message}
        requestId={data.error?.requestId}
        onRetry={() => void store.reload("initial")}
      />
    );
  } else if (count === 0) {
    body = <FirstRun canCreate={canCreate} secretWritesBlocked={secretWritesBlocked} onAdd={openCreate} />;
  } else {
    body = (
      <>
        <InlineNotice icon={Lock} className="mb-3">
          {data.alertRecipient ? (
            <>
              Alert emails go to <span className="text-gray-200">{data.alertRecipient}</span>.
            </>
          ) : (
            <>No alert email on file — contact support to add one. Alerts still appear on this page.</>
          )}{" "}
          Signing secrets are shown once and stored encrypted.
        </InlineNotice>

        {atLimit && (
          <InlineNotice className="mb-3">
            You've reached the {limit}-endpoint limit. Delete an endpoint to add another.
          </InlineNotice>
        )}

        <ul className="space-y-3">
          {data.endpoints.map((ep) => (
            <li key={ep.id}>
              <EndpointCard
                wallet={wallet}
                email={email}
                endpoint={ep}
                alertRecipient={data.alertRecipient}
                secretWritesBlocked={secretWritesBlocked}
                onChanged={store.replaceEndpoint}
                onDeleted={(id) => {
                  store.removeEndpoint(id);
                  onDismissReveal(id);
                  if (lostId === id) setLostId(null);
                }}
                onSecretRevealed={(s) => {
                  onReveal({ ...s, kind: "rotated" });
                  if (lostId === s.endpointId) setLostId(null);
                }}
                onTestSent={onTestSent}
              />
            </li>
          ))}
        </ul>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[11px] text-gray-500">
          <span>Numbers cover live deliveries in the last 24 hours.</span>
          <button
            type="button"
            onClick={() => void store.reload("stats")}
            disabled={data.refreshing}
            className={`inline-flex items-center gap-1.5 py-1.5 text-xs text-gray-400 hover:text-white disabled:opacity-60 rounded-sm ${focusRing}`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${data.refreshing ? "animate-spin" : ""}`} aria-hidden="true" />
            {data.refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Section
        icon={<Send className="w-4 h-4 text-[#61CE70]" />}
        title="Endpoints"
        desc="Up to 5 HTTPS endpoints, each with its own signing secret."
        action={addButton}
      >
        {reveals.map((r) => (
          <SecretRevealCard key={r.nonce} reveal={r} onDismiss={() => onDismissReveal(r.endpointId)} />
        ))}

        {lost && (
          <InlineNotice
            tone="warn"
            role="alert"
            className="mb-4"
            title={`${lost.urlHost || hostOf(lost.url)} was added, but its signing secret never reached your browser.`}
            action={
              <button
                type="button"
                onClick={() => setRotateLostOpen(true)}
                disabled={secretWritesBlocked}
                className={`inline-flex items-center gap-1.5 py-1 text-xs font-semibold text-amber-300 hover:text-white disabled:opacity-60 rounded-sm ${focusRing}`}
              >
                <KeyRound className="w-3.5 h-3.5" /> Rotate secret now
              </button>
            }
          >
            The connection dropped before we could show it. Rotate the secret to get a new one — the endpoint
            itself is saved and ready.
          </InlineNotice>
        )}

        {body}
      </Section>

      <EndpointFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        wallet={wallet}
        email={email}
        mode="create"
        alertRecipient={data.alertRecipient}
        onCreated={({ endpoint, secret }) => {
          store.addEndpoint({ ...endpoint, stats24h: endpoint.stats24h ?? ZERO_STATS });
          onReveal({
            endpointId: endpoint.id,
            host: endpoint.urlHost || hostOf(endpoint.url),
            secret,
            kind: "created",
            previousSecretExpiresAt: null,
          });
          toast.success("Endpoint added.");
        }}
        onCreateUnconfirmed={async () => {
          const created = await store.recoverCreate(knownIds.current);
          if (created) setLostId(created.id);
          return !!created;
        }}
      />

      <RotateSecretModal
        open={rotateLostOpen && !!lost}
        onClose={() => setRotateLostOpen(false)}
        wallet={wallet}
        email={email}
        endpoint={lost}
        secretWritesBlocked={secretWritesBlocked}
        onRotated={(r) => {
          store.replaceEndpoint(r.endpoint);
          onReveal({
            endpointId: r.endpoint.id,
            host: r.endpoint.urlHost || hostOf(r.endpoint.url),
            secret: r.secret,
            kind: "rotated",
            previousSecretExpiresAt: r.previousSecretExpiresAt,
          });
          setLostId(null);
          toast.success("Signing secret rotated.");
        }}
      />
    </>
  );
}

// ─── First run ─────────────────────────────────────────────────────────────

const STEPS: { title: string; body: string }[] = [
  {
    title: "Add an endpoint",
    body: "The HTTPS URL on your server that should receive checkout events.",
  },
  {
    title: "Save the signing secret",
    body: "Shown once. Store it as an environment variable such as CASHTAP_WEBHOOK_SECRET.",
  },
  {
    title: "Verify every request",
    body: "Check the X-CashTap-Signature header against the raw body, then answer with any 2xx.",
  },
];

function FirstRun({
  canCreate,
  secretWritesBlocked,
  onAdd,
}: {
  canCreate: boolean;
  secretWritesBlocked: boolean;
  onAdd: () => void;
}) {
  return (
    <div className="rounded-xl border border-dashed border-gray-700/60 bg-gray-900/20 p-5 sm:p-6">
      <div className="text-center">
        <Send className="w-5 h-5 mx-auto text-gray-600 mb-2" aria-hidden="true" />
        <p className="text-gray-300 text-sm font-medium">No endpoints yet.</p>
        <p className="text-gray-500 text-xs mt-1 max-w-md mx-auto leading-relaxed">
          Add your server's HTTPS URL to get a signed POST the moment a checkout session completes, expires
          or fails.
        </p>
      </div>

      <ol className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {STEPS.map((s, i) => (
          <li key={s.title} className="rounded-xl bg-black/20 border border-gray-800/60 p-4 min-w-0">
            <div className="flex items-center gap-2.5">
              <span
                aria-hidden="true"
                className="w-6 h-6 rounded-full bg-[#61CE70]/10 border border-[#61CE70]/30 text-[#61CE70] text-xs font-bold flex items-center justify-center shrink-0 tabular-nums"
              >
                {i + 1}
              </span>
              <p className="text-white text-sm font-semibold">
                <span className="sr-only">Step {i + 1}: </span>
                {s.title}
              </p>
            </div>
            <p className="mt-2 text-gray-400 text-xs leading-relaxed break-words">{s.body}</p>
          </li>
        ))}
      </ol>

      <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-3">
        <button
          type="button"
          onClick={onAdd}
          disabled={!canCreate}
          className={`${primaryBtn} disabled:cursor-not-allowed ${focusRing}`}
        >
          <Plus className="w-4 h-4" /> Add endpoint
        </button>
        <a
          href="/developers/docs#webhooks"
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-1.5 py-2 text-xs font-medium text-gray-400 hover:text-white rounded-sm ${focusRing}`}
        >
          <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> Read the webhook guide
          <ExternalLink className="w-3 h-3" aria-hidden="true" />
        </a>
      </div>
      {secretWritesBlocked && (
        <p className="mt-3 text-center text-xs text-amber-300/90">
          Creating endpoints is paused on our side right now. Please try again in a few minutes.
        </p>
      )}
    </div>
  );
}
